"use client";
import { H3 } from "../headers";
import PhoneInput from "../phoneInput";
import { Button } from "../ui/button";
import { useForm } from "react-hook-form";
import config from "@/lib/config";
import ehjzleeInstance from "@/lib/ehjzleeInstance";
import { DrawerTitle } from "../ui/drawer";
import { useDispatch } from "react-redux";
import { isValidPhoneNumber } from "react-phone-number-input";
import { login } from "@/redux/slices/login.slice";
import { FormField } from "../ui/form";
import { FloatInput } from "../ui/input";
import { useState } from "react";
import { useTranslations } from "next-intl";
import CircularProgress from "../CircularProgress";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

const LoginStep = ({
  onAfterSubmit,
}: {
  onAfterSubmit: (mobile: string) => void;
}) => {
  const [loginWithPass, setLoginWithPass] = useState(false);
  const t = useTranslations();

  return (
    <>
      <DrawerTitle>{t("loginStepSection.welcome")}</DrawerTitle>
      <H3 className="max-sm:text-center">
        {t("loginStepSection.loginPrompt")}
      </H3>
      {loginWithPass ? (
        <LoginWithPassword />
      ) : (
        <LoginWithPhone onAfterSubmit={onAfterSubmit} />
      )}
      <Button
        onClick={() => setLoginWithPass((prev) => !prev)}
        variant={"link"}
        className="text-sm mx-auto block mb-2"
      >
        {loginWithPass
          ? t("loginStepSection.verifyButton")
          : t("loginStepSection.submitButton")}
      </Button>

      {!loginWithPass ? (
        <span className="text-sm font-light block text-center">
          {t("loginStepSection.otpInfo")}
        </span>
      ) : null}
    </>
  );
};

export default LoginStep;

const LoginWithPhone = ({
  onAfterSubmit,
}: {
  onAfterSubmit: (mobile: string) => void;
}) => {
  const t = useTranslations();
  const { control, handleSubmit, formState, reset } = useForm({
    mode: "all",
    defaultValues: { mobile: "" },
    resolver: yupResolver(
      yup.object({
        mobile: yup
          .string()
          .required(t("form.requiredPhone"))
          .test({
            test: (value) => isValidPhoneNumber(value),
            message: t("form.invalidPhone"),
          }),
      })
    ),
  });

  const onSubmit = async (data: any) => {
    try {
      await ehjzleeInstance.post(config.generate_otp, data);
      onAfterSubmit(data.mobile);
    } catch (err) {
      console.log(err);
      reset({
        mobile: "",
      });
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-7 mt-7 mb-2"
    >
      <PhoneInput name="mobile" control={control} />

      <Button
        type="submit"
        disabled={!formState.isValid || formState.isSubmitting}
      >
        {formState.isSubmitting ? (
          <CircularProgress />
        ) : (
          t("loginStepSection.submitButton2")
        )}
      </Button>
    </form>
  );
};

const LoginWithPassword = () => {
  const dispatch = useDispatch();
  const t = useTranslations();

  const { control, handleSubmit, formState, reset } = useForm({
    mode: "all",
    defaultValues: {
      mobile: "",
      password: "",
    },
    resolver: yupResolver(
      yup.object({
        mobile: yup
          .string()
          .required(t("form.requiredPhone"))
          .test({
            test: (value) => isValidPhoneNumber(value, "SA"),
            message: t("form.invalidPhone"),
          }),
        password: yup.string().required(t("form.requiredPass")).min(8),
      })
    ),
  });

  const onSubmit = async (data: any) => {
    try {
      const loginRes = await ehjzleeInstance.post(config.login, data);
      dispatch(login(loginRes.data));
    } catch (err) {
      console.log(err);
      reset({
        mobile: "",
        password: "",
      });
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="flex flex-col gap-7 mt-7 mb-2"
    >
      <PhoneInput name="mobile" control={control} />

      <FormField
        name="password"
        control={control}
        render={({ field, fieldState }) => (
          <FloatInput
            type="password"
            autoComplete="new-password"
            {...field}
            placeholder={t("loginStepSection.password")}
            hasError={fieldState.invalid}
            errorMessage={fieldState.error?.message}
          />
        )}
      />

      <Button
        type="submit"
        disabled={!formState.isValid || formState.isSubmitting}
      >
        {formState.isSubmitting ? (
          <CircularProgress />
        ) : (
          t("loginStepSection.Login")
        )}
      </Button>
    </form>
  );
};
